import {Component, computed, inject, signal} from '@angular/core';
import {injectQuery} from "@tanstack/angular-query-experimental";
import {HttpClient} from "@angular/common/http";
import {lastValueFrom} from "rxjs";
import {environment} from "../../environments/environment";
import {LanguageService} from "../_services/language.service";
import {toSignal} from "@angular/core/rxjs-interop";

interface Alert {
  id: number;
  html: string;
}

/**
 * Alert HTML can contain hrefs serialized as `\"https://...\"` (or with an
 * `unsafe:` prefix). Unwrap that specific representation so Angular can
 * sanitize the actual URL.
 */
export function normalizeAlertHtml(html: string): string {
  return html
    .replace(
      /(\bhref\s*=\s*)(["'])(?:unsafe:\s*)?\\?["'](https?:\/\/[^"'\\]+?)(?:\\?["'])(\2)/gi,
      '$1$2$3$4',
    )
    .replace(/\\(["'])/g, '$1');
}

@Component({
  selector: 'app-alerts-bar',
  imports: [],
  templateUrl: './alerts-bar.component.html',
  styleUrl: './alerts-bar.component.scss',
})
export class AlertsBarComponent {
  private readonly http = inject(HttpClient);
  private readonly languageService = inject(LanguageService);
  private readonly language = toSignal(this.languageService.currentMessage, {
    initialValue: this.languageService.getActiveLanguage(),
  });

  protected readonly expanded = signal(false);

  protected query = injectQuery(() => ({
    queryKey: ['alerts', this.language()],
    queryFn: () => lastValueFrom(this.http.get<Alert[]>(`${environment.assessorSiteUrl}/api/Alert/ListActive`, {
      params: {language: this.language()},
    })),
  }));

  alerts = computed<Alert[]>(() =>
    (this.query.data() ?? []).map(alert => ({
      ...alert,
      html: normalizeAlertHtml(alert.html),
    })),
  );
  hasAlerts = computed(() => this.alerts().length > 0);

  protected toggle(): void {
    this.expanded.update(isExpanded => !isExpanded);
  }
}
